import classNames from "classnames";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Image } from "../../../components/elements";
import { IBannerItem } from "../../../contexts/ConfigContext/types";
import { PAGE_PARAM } from "../../constants";
import "./Banner.scss";
import BannerSingle from "./BannerSingle";
import "./Banner_flint.scss";
import "./Banner_kokoro.scss";
import "./Banner_pdd.scss";
import "./Banner_shabuDays.scss";
import "./Banner_torikizoku.scss";
import "./Banner_wang.scss";

interface IBannerProps {
	images: IBannerItem[];
	handleCategoryClick: (categoryId: string) => void;
	handleItemClick: (
		brand_menu_item_id: number,
		mode?: PAGE_PARAM,
		cart_item_index?: string
	) => void;
	"data-testid"?: string;
	className?: string;
}

const AUTOSLIDE_INTERVAL_MS = 5000;
const SWIPE_THRESHOLD = 50;
const DRAG_RESISTANCE = 0.5; // Resistance factor for edge dragging
const VELOCITY_THRESHOLD = 0.5; // Velocity threshold for quick swipes

const Banner: React.FC<IBannerProps> = ({
	images: propImages,
	handleCategoryClick,
	handleItemClick,
	className,
	"data-testid": dataTestId,
}) => {
	const navigate = useNavigate();
	const [currentIndex, setCurrentIndex] = useState(0);
	const [touchStartX, setTouchStartX] = useState<number | null>(null);
	const [touchEndX, setTouchEndX] = useState<number | null>(null);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [mouseStartX, setMouseStartX] = useState<number | null>(null);
	const [mouseEndX, setMouseEndX] = useState<number | null>(null);
	const [dragOffset, setDragOffset] = useState(0);
	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const bannerRef = useRef<HTMLDivElement>(null);
	const lastMoveTime = useRef<number>(0);
	const lastMoveX = useRef<number>(0);
	const velocity = useRef<number>(0);

	const images = useMemo(() => {
		const uniqueImages: IBannerItem[] = [];
		const seenIds = new Set<string>();

		for (const image of propImages) {
			if (!seenIds.has(image.id)) {
				seenIds.add(image.id);
				uniqueImages.push(image);
			}
		}

		return uniqueImages;
	}, [propImages]);

	useEffect(() => {
		if (
			(images.length > 0 && currentIndex >= images.length) ||
			(images.length === 0 && currentIndex !== 0)
		) {
			setCurrentIndex(0);
		}
	}, [images.length, currentIndex]);

	const nextSlide = useCallback(() => {
		if (images.length === 0 || isTransitioning) return;
		setIsTransitioning(true);
		setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
		setDragOffset(0);
		setTimeout(() => {
			setIsTransitioning(false);
		}, 300);
	}, [images.length, isTransitioning]);

	const prevSlide = useCallback(() => {
		if (images.length === 0 || isTransitioning) return;
		setIsTransitioning(true);
		setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
		setDragOffset(0);
		setTimeout(() => {
			setIsTransitioning(false);
		}, 300);
	}, [images.length, isTransitioning]);

	const handleImageClick = useCallback(
		(bannerData: IBannerItem) => {
			if (!bannerData.bannerClickData) {
				return;
			}

			const { type, data } = bannerData.bannerClickData;

			switch (type) {
				case "category":
					handleCategoryClick(data);
					break;
				case "item":
					handleItemClick(Number(data))();
					break;
			}
		},
		[handleCategoryClick, navigate]
	);

	const handleTouchStart = useCallback(
		(event: React.TouchEvent) => {
			if (images.length <= 1 || isTransitioning) return;
			const startX = event.touches[0].clientX;

			setTouchStartX(startX);
			setDragOffset(0);
			// Initialize tracking
			lastMoveTime.current = Date.now();
			lastMoveX.current = startX;
			velocity.current = 0;
		},
		[images.length, isTransitioning]
	);

	const handleTouchMove = useCallback(
		(event: React.TouchEvent) => {
			if (images.length <= 1 || isTransitioning || touchStartX === null) return;
			const currentX = event.touches[0].clientX;
			const currentTime = Date.now();

			// Calculate velocity
			if (lastMoveTime.current > 0) {
				const timeDiff = currentTime - lastMoveTime.current;
				const moveDiff = currentX - lastMoveX.current;

				velocity.current = moveDiff / timeDiff;
			}

			lastMoveTime.current = currentTime;
			lastMoveX.current = currentX;

			setTouchEndX(currentX);

			// Apply smooth drag offset with edge resistance
			let offset = currentX - touchStartX;

			// Apply resistance at edges
			if (
				(currentIndex === 0 && offset > 0) ||
				(currentIndex === images.length - 1 && offset < 0)
			) {
				offset = offset * DRAG_RESISTANCE;
			}

			setDragOffset(offset);
		},
		[images.length, isTransitioning, touchStartX, currentIndex]
	);

	const handleTouchEnd = useCallback(() => {
		if (touchStartX === null || touchEndX === null || images.length <= 1 || isTransitioning) return;
		const diff = touchStartX - touchEndX;
		const currentVelocity = velocity.current;

		// Use velocity for quick swipes or distance for slow drags
		if (Math.abs(currentVelocity) > VELOCITY_THRESHOLD || Math.abs(diff) > SWIPE_THRESHOLD) {
			if (currentVelocity < -VELOCITY_THRESHOLD || diff > SWIPE_THRESHOLD) {
				nextSlide();
			} else if (currentVelocity > VELOCITY_THRESHOLD || diff < -SWIPE_THRESHOLD) {
				prevSlide();
			}
		}

		// Reset values
		setTouchStartX(null);
		setTouchEndX(null);
		setDragOffset(0);
		velocity.current = 0;
		lastMoveTime.current = 0;
		lastMoveX.current = 0;
	}, [touchStartX, touchEndX, images.length, isTransitioning, nextSlide, prevSlide]);

	const handleMouseDown = useCallback(
		(event: React.MouseEvent) => {
			if (images.length <= 1 || isTransitioning) return;
			const startX = event.clientX;

			setIsDragging(true);
			setMouseStartX(startX);
			setDragOffset(0);
			// Initialize tracking
			lastMoveTime.current = Date.now();
			lastMoveX.current = startX;
			velocity.current = 0;
			event.preventDefault();
		},
		[images.length, isTransitioning]
	);

	const handleMouseMove = useCallback(
		(event: React.MouseEvent) => {
			if (!isDragging || images.length <= 1 || isTransitioning || mouseStartX === null) return;
			const currentX = event.clientX;
			const currentTime = Date.now();

			// Calculate velocity
			if (lastMoveTime.current > 0) {
				const timeDiff = currentTime - lastMoveTime.current;
				const moveDiff = currentX - lastMoveX.current;

				velocity.current = moveDiff / timeDiff;
			}

			lastMoveTime.current = currentTime;
			lastMoveX.current = currentX;

			setMouseEndX(currentX);

			// Apply smooth drag offset with edge resistance
			let offset = currentX - mouseStartX;

			// Apply resistance at edges
			if (
				(currentIndex === 0 && offset > 0) ||
				(currentIndex === images.length - 1 && offset < 0)
			) {
				offset = offset * DRAG_RESISTANCE;
			}

			setDragOffset(offset);
			event.preventDefault();
		},
		[isDragging, images.length, isTransitioning, mouseStartX, currentIndex]
	);

	const handleMouseUp = useCallback(() => {
		if (!isDragging) return;

		if (mouseStartX !== null && mouseEndX !== null && images.length > 1 && !isTransitioning) {
			const diff = mouseStartX - mouseEndX;
			const currentVelocity = velocity.current;

			// Use velocity for quick swipes or distance for slow drags
			if (Math.abs(currentVelocity) > VELOCITY_THRESHOLD || Math.abs(diff) > SWIPE_THRESHOLD) {
				if (currentVelocity < -VELOCITY_THRESHOLD || diff > SWIPE_THRESHOLD) {
					nextSlide();
				} else if (currentVelocity > VELOCITY_THRESHOLD || diff < -SWIPE_THRESHOLD) {
					prevSlide();
				}
			}
		}

		// Reset values
		setIsDragging(false);
		setMouseStartX(null);
		setMouseEndX(null);
		setDragOffset(0);
		velocity.current = 0;
		lastMoveTime.current = 0;
		lastMoveX.current = 0;
	}, [isDragging, mouseStartX, mouseEndX, images.length, isTransitioning, nextSlide, prevSlide]);

	const handleMouseLeave = useCallback(() => {
		if (isDragging) {
			setIsDragging(false);
			setMouseStartX(null);
			setMouseEndX(null);
			setDragOffset(0);
			velocity.current = 0;
			lastMoveTime.current = 0;
			lastMoveX.current = 0;
		}
	}, [isDragging]);

	const handleIndicatorClick = useCallback(
		(index: number) => {
			if (isTransitioning) return;

			setIsTransitioning(true);
			setCurrentIndex(index);
			setDragOffset(0);
			setTimeout(() => {
				setIsTransitioning(false);
			}, 300);
		},
		[isTransitioning]
	);

	useEffect(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		if (images.length > 1) {
			timerRef.current = setTimeout(nextSlide, AUTOSLIDE_INTERVAL_MS);
		}

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [currentIndex, nextSlide, images.length]);

	if (images.length === 0) {
		return (
			<div ref={bannerRef} className={className} data-testid={dataTestId}>
				<div className="BannerSlides" data-testid="banner-slides" />
				<div className="BannerIndicators" data-testid="banner-indicators" />
			</div>
		);
	}

	if (images.length === 1) {
		return (
			<BannerSingle
				image={images[0]}
				handleCategoryClick={handleCategoryClick}
				handleItemClick={handleItemClick}
				className={className}
				data-testid={dataTestId}
			/>
		);
	}

	return (
		<div
			ref={bannerRef}
			className={classNames("Banner", className, { Dragging: isDragging })}
			onTouchStart={handleTouchStart}
			onTouchMove={handleTouchMove}
			onTouchEnd={handleTouchEnd}
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
			onMouseLeave={handleMouseLeave}
			aria-roledescription="carousel"
			aria-label="Image banner"
			data-testid={dataTestId}
			aria-hidden
		>
			<div
				className="BannerSlides"
				data-testid="banner-slides"
				style={{
					transform: `translateX(${dragOffset}px)`,
				}}
			>
				{images.map((image, index) => {
					const isPrevious = index === (currentIndex - 1 + images.length) % images.length;
					const isNext = index === (currentIndex + 1) % images.length;
					const isCurrent = index === currentIndex;

					return (
						<Image
							key={`banner-slide-${image.id}`}
							src={image.src}
							alt={`Banner ${index + 1}`}
							className={classNames("BannerSlide", {
								Active: isCurrent,
								PreviousEdge: isPrevious,
								NextEdge: isNext,
								Clickable: isCurrent && image.bannerClickData,
							})}
							onClick={
								isCurrent && image.bannerClickData ? () => handleImageClick(image) : undefined
							}
							data-testid={`banner-image-${index}`}
						/>
					);
				})}
			</div>

			{images.length > 1 && (
				<div className="BannerIndicators" data-testid="banner-indicators">
					{images.map((image, index) => (
						<Button
							key={`banner-indicator-${image.id}`}
							className={classNames("BannerIndicator", "Btn", {
								Active: index === currentIndex,
							})}
							onClick={() => handleIndicatorClick(index)}
							data-testid={`banner-indicator-${index}`}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export default memo(Banner);
